import React from 'react';
import { Col, FormFeedback, FormGroup, FormText, Input, Label, Row } from 'reactstrap';
import { FormikErrors, FormikTouched } from 'formik';

import Icon, { faTriangleExclamation } from '~components/icon';

/**
 * Field-tag component registry for the DEX tab.
 *
 * The API resolves an appointment's stable dexServiceDEXID to an ordered list of field-component
 * tags (see dex_service_field_map / resolveDexFieldMap in tp-assist-api). Each tag here maps to the
 * React field that renders and edits it. The tab renders the union of components for the returned
 * tags, in the tag order the API gives - replacing the old single-category config lookup.
 */

export const DEX_SESSION_LOCATION = { HOME: 1, COMMUNITY: 2 } as const;

export type DexFieldTag =
  | 'subcontractor'
  | 'volunteer'
  | 'session_location'
  | 'overnight'
  | 'meals_provided'
  | 'meals_cooked_offsite'
  | 'texture_modified'
  | 'meal_type'
  | 'engagement_time'
  | 'trip_distance'
  | 'trip_travel_time'
  | 'total_cost'
  | 'extra_items';

// Which appointment_dex_details columns each field tag covers. Mirrors the API's own
// DEX_FIELD_TAG_COLUMNS (dex_copy_fields.mjs) - the two are kept in sync by hand, as the header
// comment there notes, since no shared source of truth exists between the two codebases.
export const DEX_FIELD_TAG_COLUMNS: Record<string, string[]> = {
  subcontractor: ['subcontractor'],
  volunteer: ['volunteer'],
  session_location: ['session_location'],
  overnight: ['overnight'],
  meals_provided: ['meals_provided'],
  meals_cooked_offsite: ['meals_cooked_offsite'],
  texture_modified: ['texture_modified'],
  meal_type: ['meal_type_main', 'meal_type_light', 'meal_type_dessert', 'meal_type_beverage', 'meal_type_snack'],
  engagement_time: ['engagement_time_minutes'],
  trip_distance: ['trip_distance_km'],
  trip_travel_time: ['trip_travel_time_minutes'],
  total_cost: ['total_cost'],
  extra_items: ['extra_items'],
};

// TAV-3476: DEX's accepted Extra item options (DEXReferenceData.xml, CodeType "ExtraItem"). Staff
// see the description; the code is what is saved and exported, inner spaces included. Mirrors the
// API's DEX_EXTRA_ITEMS (constants/dex_codes.mjs), which rejects any other code, and is in the
// same order, the order the API stores selections in.
export const DEX_EXTRA_ITEM_OPTIONS: { code: string; description: string }[] = [
  { code: 'TOILET', description: 'Toileting products' },
  { code: 'BATHROOM PRODUCTS', description: 'Bathroom products' },
  { code: 'LIGHT', description: 'Light fixtures' },
  { code: 'SUPPORT RAILS', description: 'Supporting rails and grab bars' },
  { code: 'CONSTRUCTIONS', description: 'Construction elements in homes' },
  { code: 'VERTICAL', description: 'Vertical accessibility' },
  { code: 'SAFETY', description: 'Safety equipment for homes and other premises' },
  { code: 'STORAGE', description: 'Furniture for storage' },
  { code: 'MECHANICAL', description: 'Mechanical products for operation and controlling devices' },
  { code: 'OPENERS', description: 'Gate, door, window and curtain openers/closers' },
  { code: 'ELECTRONIC', description: 'Electronic products for operation and controlling devices' },
  { code: 'OTHER', description: 'Other' },
];

// DEX's own guidance: tick every item that applies, but no more than this many. Advisory only.
export const DEX_EXTRA_ITEMS_RECOMMENDED_MAX = 3;

// Tags whose visibility follows the meal-details gate: they always show unless the tag list also
// carries meals_provided (Respite), in which case they only show once meals_provided is checked.
export const MEAL_GATED_TAGS: DexFieldTag[] = ['texture_modified', 'meal_type'];

export interface DexFormValues {
  subcontractor: string;
  volunteer: boolean;
  session_location: string;
  overnight: boolean;
  meals_provided: boolean;
  meals_cooked_offsite: boolean;
  texture_modified: boolean;
  meal_type_main: string;
  meal_type_light: string;
  meal_type_dessert: string;
  meal_type_beverage: string;
  meal_type_snack: string;
  engagement_time_minutes: string;
  trip_distance_km: string;
  trip_travel_time_minutes: string;
  total_cost: string;
  extra_items: string[];
}

export interface DexFieldProps {
  values: DexFormValues;
  errors: FormikErrors<DexFormValues>;
  touched: FormikTouched<DexFormValues>;
  handleChange: React.ChangeEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

const CheckboxField: React.FC<{
  id: string;
  name: keyof DexFormValues;
  label: string;
  checked: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}> = ({ id, name, label, checked, onChange }) => (
  <Row className="mt-3">
    <Col md={3}>
      <FormGroup check>
        <Input type="checkbox" id={id} name={name} checked={checked} onChange={onChange} data-cy={`${id}-checkbox`} />
        <Label check for={id}>
          {label}
        </Label>
      </FormGroup>
    </Col>
  </Row>
);

const MEAL_COUNT_FIELDS: { name: keyof DexFormValues; label: string; id: string }[] = [
  { name: 'meal_type_main', label: 'Main', id: 'dex-meal-type-main' },
  { name: 'meal_type_light', label: 'Light', id: 'dex-meal-type-light' },
  { name: 'meal_type_dessert', label: 'Dessert', id: 'dex-meal-type-dessert' },
  { name: 'meal_type_beverage', label: 'Beverage', id: 'dex-meal-type-beverage' },
  { name: 'meal_type_snack', label: 'Snack', id: 'dex-meal-type-snack' },
];

const SubcontractorField: React.FC<DexFieldProps> = ({ values, errors, touched, handleChange, handleBlur }) => (
  <Row className="mt-3">
    <Col md={4}>
      <FormGroup>
        <Label for="dex-subcontractor">Subcontractor</Label>
        <Input
          type="select"
          id="dex-subcontractor"
          name="subcontractor"
          value={values.subcontractor}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={touched.subcontractor && !!errors.subcontractor}
          data-cy="dex-subcontractor-select"
        >
          <option value="">- Select -</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Input>
        {touched.subcontractor && errors.subcontractor && <FormFeedback>{errors.subcontractor}</FormFeedback>}
      </FormGroup>
    </Col>
  </Row>
);

const VolunteerField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <CheckboxField
    id="dex-volunteer"
    name="volunteer"
    label="Volunteer"
    checked={values.volunteer}
    onChange={handleChange}
  />
);

const SessionLocationField: React.FC<DexFieldProps> = ({ values, errors, touched, handleChange, handleBlur }) => (
  <Row className="mt-3">
    <Col md={4}>
      <FormGroup>
        <Label for="dex-session-location">Session Location</Label>
        <Input
          type="select"
          id="dex-session-location"
          name="session_location"
          value={values.session_location}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={touched.session_location && !!errors.session_location}
          data-cy="dex-session-location-select"
        >
          <option value="">- Select -</option>
          <option value={DEX_SESSION_LOCATION.HOME}>Home</option>
          <option value={DEX_SESSION_LOCATION.COMMUNITY}>Community</option>
        </Input>
        {touched.session_location && errors.session_location && <FormFeedback>{errors.session_location}</FormFeedback>}
      </FormGroup>
    </Col>
  </Row>
);

const OvernightField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <CheckboxField
    id="dex-overnight"
    name="overnight"
    label="Overnight"
    checked={values.overnight}
    onChange={handleChange}
  />
);

const MealsProvidedField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <CheckboxField
    id="dex-meals-provided"
    name="meals_provided"
    label="Meals Provided"
    checked={values.meals_provided}
    onChange={handleChange}
  />
);

const MealsCookedOffsiteField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <CheckboxField
    id="dex-meals-cooked-offsite"
    name="meals_cooked_offsite"
    label="Meals Cooked Offsite"
    checked={values.meals_cooked_offsite}
    onChange={handleChange}
  />
);

const TextureModifiedField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <CheckboxField
    id="dex-texture-modified"
    name="texture_modified"
    label="Texture Modified"
    checked={values.texture_modified}
    onChange={handleChange}
  />
);

// Composite meal_type tag: renders all five meal count inputs as a single unit.
const MealTypeField: React.FC<DexFieldProps> = ({ values, errors, touched, handleChange, handleBlur }) => (
  <Row className="mt-3">
    {MEAL_COUNT_FIELDS.map(({ name, label, id }) => (
      <Col md={2} key={name}>
        <FormGroup>
          <Label for={id}>{label}</Label>
          <Input
            type="number"
            id={id}
            name={name}
            min={0}
            value={values[name] as string}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched[name] && !!errors[name]}
            data-cy={`${id}-input`}
          />
          {touched[name] && errors[name] && <FormFeedback>{errors[name] as string}</FormFeedback>}
        </FormGroup>
      </Col>
    ))}
  </Row>
);

const EngagementTimeField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <Row className="mt-3">
    <Col md={3}>
      <FormGroup>
        <Label for="dex-engagement-time-minutes">Engagement Time (minutes)</Label>
        <Input
          type="number"
          id="dex-engagement-time-minutes"
          name="engagement_time_minutes"
          min={0}
          value={values.engagement_time_minutes}
          onChange={handleChange}
          data-cy="dex-engagement-time-minutes-input"
        />
      </FormGroup>
    </Col>
  </Row>
);

const TripDistanceField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <Row className="mt-3">
    <Col md={3}>
      <FormGroup>
        <Label for="dex-trip-distance-km">Trip Distance (km)</Label>
        <Input
          type="number"
          id="dex-trip-distance-km"
          name="trip_distance_km"
          min={0}
          step="0.01"
          value={values.trip_distance_km}
          onChange={handleChange}
          data-cy="dex-trip-distance-km-input"
        />
      </FormGroup>
    </Col>
  </Row>
);

const TripTravelTimeField: React.FC<DexFieldProps> = ({ values, handleChange }) => (
  <Row className="mt-3">
    <Col md={3}>
      <FormGroup>
        <Label for="dex-trip-travel-time-minutes">Trip Travel Time (minutes)</Label>
        <Input
          type="number"
          id="dex-trip-travel-time-minutes"
          name="trip_travel_time_minutes"
          min={0}
          value={values.trip_travel_time_minutes}
          onChange={handleChange}
          data-cy="dex-trip-travel-time-minutes-input"
        />
      </FormGroup>
    </Col>
  </Row>
);

const TotalCostField: React.FC<DexFieldProps> = ({ values, errors, touched, handleChange, handleBlur }) => (
  <Row className="mt-3">
    <Col md={3}>
      <FormGroup>
        <Label for="dex-total-cost">Total Cost ($)</Label>
        <Input
          type="number"
          id="dex-total-cost"
          name="total_cost"
          min={0}
          step="1"
          value={values.total_cost}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={touched.total_cost && !!errors.total_cost}
          data-cy="dex-total-cost-input"
        />
        {touched.total_cost && errors.total_cost && <FormFeedback>{errors.total_cost}</FormFeedback>}
      </FormGroup>
    </Col>
  </Row>
);

// Same-named checkboxes with a value: Formik's handleChange adds or removes that value from the
// extra_items array. Option ids use the index, as codes contain spaces.
const ExtraItemsField: React.FC<DexFieldProps> = ({ values, errors, touched, handleChange, handleBlur }) => {
  const error = touched.extra_items && typeof errors.extra_items === 'string' ? errors.extra_items : undefined;
  const overRecommended = values.extra_items.length > DEX_EXTRA_ITEMS_RECOMMENDED_MAX;
  const describedBy = [
    'dex-extra-items-required',
    error ? 'dex-extra-items-error' : null,
    overRecommended ? 'dex-extra-items-advice' : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Row className="mt-3">
      <Col md={6}>
        <span id="dex-extra-items-label" className="form-label d-block">
          Extra Item Detail
        </span>
        <div
          role="group"
          aria-labelledby="dex-extra-items-label"
          aria-invalid={!!error}
          aria-describedby={describedBy}
          data-cy="dex-extra-items-group"
        >
          <span id="dex-extra-items-required" className="visually-hidden">
            Required
          </span>
          {DEX_EXTRA_ITEM_OPTIONS.map(({ code, description }, index) => (
            <FormGroup check key={code}>
              <Input
                type="checkbox"
                id={`dex-extra-item-${index}`}
                name="extra_items"
                value={code}
                checked={values.extra_items.includes(code)}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={!!error}
                data-cy={`dex-extra-item-${index}-checkbox`}
              />
              <Label check for={`dex-extra-item-${index}`}>
                {description}
              </Label>
            </FormGroup>
          ))}
        </div>
        {error && (
          <FormFeedback id="dex-extra-items-error" className="d-block">
            {error}
          </FormFeedback>
        )}
        {overRecommended && (
          // Muted text with a warning-coloured icon: text-warning body copy fails WCAG AA contrast,
          // while the icon (non-text, decorative - aria-hidden by default) only needs 3:1.
          <FormText id="dex-extra-items-advice">
            <Icon icon={faTriangleExclamation} className="text-warning me-1" data-cy="dex-extra-items-advice-icon" />
            DEX recommends selecting no more than {DEX_EXTRA_ITEMS_RECOMMENDED_MAX} items.
          </FormText>
        )}
      </Col>
    </Row>
  );
};

export const DEX_FIELD_COMPONENTS: Record<DexFieldTag, React.FC<DexFieldProps>> = {
  subcontractor: SubcontractorField,
  volunteer: VolunteerField,
  session_location: SessionLocationField,
  overnight: OvernightField,
  meals_provided: MealsProvidedField,
  meals_cooked_offsite: MealsCookedOffsiteField,
  texture_modified: TextureModifiedField,
  meal_type: MealTypeField,
  engagement_time: EngagementTimeField,
  trip_distance: TripDistanceField,
  trip_travel_time: TripTravelTimeField,
  total_cost: TotalCostField,
  extra_items: ExtraItemsField,
};

export const isDexFieldTag = (tag: string): tag is DexFieldTag =>
  Object.prototype.hasOwnProperty.call(DEX_FIELD_COMPONENTS, tag);
